/**
 * The assistant slot.
 *
 * The chat panel talks to an AssistantAdapter and does not care what is behind
 * it. Today that is the guided adapter: the deterministic intake graph, which
 * never says anything untested. A later phase adds an AI adapter (a Cloudflare
 * Worker at /api/chat) that implements the same interface, lets people ask
 * free-form questions, and gets every number by calling the same tested
 * functions the page uses — it is never allowed to do arithmetic itself.
 * See docs/PLAN.md.
 */

import { answer, isComplete, nextNode, progress, reopen, skip } from '../intake/engine.js';
import type { Answer, IntakeNode } from '../intake/graph.js';
import type { Workspace } from '../workspace/schema.js';

export interface AssistantTurn {
  /** The question to show now, or null when the guided flow is finished. */
  node: IntakeNode | null;
  progress: { done: number; total: number };
  complete: boolean;
}

export interface AssistantAdapter {
  readonly kind: 'guided' | 'ai';
  readonly label: string;
  /** What the assistant is asking right now. */
  current(ws: Workspace): AssistantTurn;
  respond(ws: Workspace, nodeId: string, a: Answer): Workspace;
  skip(ws: Workspace, nodeId: string): Workspace;
  revisit(ws: Workspace, nodeId: string): Workspace;
  /**
   * Free-form questions. The guided adapter has no answer for them and says
   * so; the AI adapter will implement this with tool calls.
   */
  ask?(ws: Workspace, question: string): Promise<string>;
}

export const guidedAdapter: AssistantAdapter = {
  kind: 'guided',
  label: 'Guided assistant',
  current: (ws) => ({ node: nextNode(ws), progress: progress(ws), complete: isComplete(ws) }),
  respond: answer,
  skip,
  revisit: reopen,
};
